import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BOOKS_PER_PAGE,
  getBooksPageCache,
  loadBooksPage,
} from '../utils/booksCache';
import NativeAffiliateBanner from './ads/NativeAffiliateBanner';

const SEARCH_DEBOUNCE_MS = 350;

const BOOK_FILTERS = [
  { value: 'all', label: 'All books' },
  { value: 'authored', label: 'Poppyruz published' },
  { value: 'public_domain', label: 'Public domain' },
];

export default function AllBooksPage({ isLoading: parentLoading = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [bookFilter, setBookFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const initialCache = getBooksPageCache({
    page: 1,
    q: '',
    filter: 'all',
  });
  const [books, setBooks] = useState(initialCache?.books || []);
  const [totalCount, setTotalCount] = useState(initialCache?.totalCount ?? 0);
  const [loading, setLoading] = useState(!initialCache);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedQuery || '';
    const filter = bookFilter;
    const page = currentPage;

    const cached = getBooksPageCache({ page, q, filter });
    if (cached) {
      setBooks(cached.books);
      setTotalCount(cached.totalCount);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    (async () => {
      try {
        const result = await loadBooksPage({ page, q, filter });
        if (cancelled) return;
        setBooks(result.books);
        setTotalCount(result.totalCount);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch books:', error);
          setBooks([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookFilter, currentPage, debouncedQuery]);

  const totalPages = Math.max(1, Math.ceil(totalCount / BOOKS_PER_PAGE));
  const showSpinner = loading || parentLoading;
  const showEmpty = !showSpinner && books.length === 0;

  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPage = (page) => setCurrentPage(page);

  const handleFilterChange = (e) => {
    setBookFilter(e.target.value);
    setCurrentPage(1);
  };

  const emptyMessage = useMemo(() => {
    if (debouncedQuery) {
      return 'No books match your search.';
    }
    if (bookFilter === 'authored') {
      return 'No Poppyruz published books available yet.';
    }
    if (bookFilter === 'public_domain') {
      return 'No public domain books available at the moment.';
    }
    return 'No books available at the moment.';
  }, [bookFilter, debouncedQuery]);

  return (
    <div className="min-h-screen px-6 py-10 bg-white text-gray-900">
      <h1 className="text-3xl font-bold text-center mb-6">Browse Books</h1>

      <div className="mx-auto mb-10 max-w-2xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <label htmlFor="public-books-search" className="sr-only">
              Search books
            </label>
            <input
              id="public-books-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by book title…"
              className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-800/30"
              autoComplete="off"
            />
          </div>
          <div className="sm:w-52 shrink-0">
            <label htmlFor="public-books-filter" className="sr-only">
              Filter books
            </label>
            <select
              id="public-books-filter"
              value={bookFilter}
              onChange={handleFilterChange}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-800/30"
            >
              {BOOK_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {!loading ? (
          <p className="mt-2 text-center text-sm text-slate-500">
            {totalCount === 0
              ? emptyMessage
              : `${totalCount} book${totalCount === 1 ? '' : 's'} found`}
          </p>
        ) : null}
      </div>

      {showSpinner ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="w-10 h-10 border-4 border-violet-800 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : showEmpty ? (
        <div className="text-center text-gray-600 mt-12">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {books.map((book) => (
            <div
              key={book.book_meta_unique_id}
              role="button"
              tabIndex={0}
              className="cursor-pointer flex flex-col items-center group"
              onClick={() => {
                window.location.href =
                  book.is_authored === 1 || book.is_authored === true
                    ? `/read_authored/${book.book_meta_unique_id}`
                    : `/read_book/${book.book_meta_unique_id}`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.location.href =
                    book.is_authored === 1 || book.is_authored === true
                      ? `/read_authored/${book.book_meta_unique_id}`
                      : `/read_book/${book.book_meta_unique_id}`;
                }
              }}
            >
              <img
                src={book.book_cover}
                alt={book.book_name}
                className="w-full h-full object-cover rounded-lg shadow-md transition-transform group-hover:scale-105"
              />
              <p className="mt-2 text-sm font-medium text-gray-800 text-center line-clamp-2">
                {book.book_name}
              </p>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && !showSpinner ? (
        <div className="mt-10 flex justify-center items-center space-x-2">
          <button
            type="button"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToPage(i + 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === i + 1 ? 'bg-violet-900 text-white' : 'bg-white text-gray-700'
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            type="button"
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      <NativeAffiliateBanner placement="books" className="mt-12 -mx-6" />
    </div>
  );
}
